import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  offset,
  shift,
  useFloating,
  useTransitionStyles,
} from '@floating-ui/react';
import type { ReactNode, SVGProps } from 'react';
import {
  Fragment,
  memo,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { FloatingPortal } from '#/components/ui/FloatingPortal';
import { Icon } from '#/components/ui/icons';
import { Spinner } from '#/components/ui/Spinner';
import { Tabs } from '#/components/ui/Tabs';
import { useDismissibleOverlay } from '#/hooks/useDismissibleOverlay';
import { useMeasureOnCommit } from '#/hooks/usePerformanceMeasure';
import { UnreachableCaseError } from '#/lib/error';
import { MDC_MEASURE } from '#/lib/performance';
import { cn } from '#/utils/cn';
import { DASH } from '#/utils/constants';
import {
  formatCompact,
  formatCurrency,
  formatDecimal,
  formatInteger,
  formatPercent,
} from '#/utils/format';

import { downsampleTimeseries } from './normalize';
import { NEW_YORK_TIME_ZONE } from './timeframes';
import type {
  ChartTimeframeId,
  ComparisonIndexId,
  NormalizedTimeseriesSeries,
  TimeseriesPoint,
  TooltipField,
} from './types';

const DEFAULT_TOOLTIP_FIELDS: TooltipField[] = [
  'close',
  'open',
  'high',
  'low',
  'volume',
];

const CHART_HEIGHT = 320;
const AXIS_FONT_SIZE = 14;
const MIN_Y_AXIS_WIDTH = 44;
const Y_AXIS_TICK_COUNT = 5;
const Y_AXIS_LABEL_CHAR_WIDTH = AXIS_FONT_SIZE * 0.56;
const Y_AXIS_LABEL_HORIZONTAL_PADDING = 10;
const Y_AXIS_LABEL_EXTRA_CHAR_COUNT = 1;
const CHART_LEFT_MARGIN = 8;

const YEAR_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: NEW_YORK_TIME_ZONE,
  year: 'numeric',
});

const MONTH_DAY_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: NEW_YORK_TIME_ZONE,
  month: 'short',
  day: 'numeric',
});

const MONTH_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: NEW_YORK_TIME_ZONE,
  month: 'short',
});

const INTRADAY_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: NEW_YORK_TIME_ZONE,
  hour: 'numeric',
  minute: '2-digit',
});

const TOOLTIP_DAY_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: NEW_YORK_TIME_ZONE,
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const TOOLTIP_DAY_TIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: NEW_YORK_TIME_ZONE,
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

function formatXAxisTick(
  timeframe: ChartTimeframeId,
  timestamp: number,
): string {
  const date = new Date(timestamp);

  switch (timeframe) {
    case '10Y':
    case '5Y':
    case 'ALL':
      return YEAR_FORMATTER.format(date);
    case 'YTD':
    case '1Y':
      return MONTH_FORMATTER.format(date);
    case '6M':
    case '1M':
    case '1W':
      return MONTH_DAY_FORMATTER.format(date);
    case 'INTRADAY':
      return INTRADAY_FORMATTER.format(date);
    default:
      throw new UnreachableCaseError(timeframe);
  }
}

function formatYAxisTick(value: number): string {
  return Number.isInteger(value)
    ? (formatInteger(value) ?? '')
    : (formatDecimal(value) ?? '');
}

function formatCompareYAxisTick(value: number): string {
  return formatPercent(value) ?? '';
}

function formatSignedPercentChange(value: number | null | undefined): string {
  if (value == null) {
    return DASH;
  }

  const formattedValue = formatPercent(Math.abs(value)) ?? '0.00%';

  if (value > 0) {
    return `+${formattedValue}`;
  }

  if (value < 0) {
    return `-${formattedValue}`;
  }

  return formattedValue;
}

function getYAxisWidth(labels: string[]): number {
  if (!labels.length) {
    return MIN_Y_AXIS_WIDTH;
  }

  const longestLabelLength = labels.reduce((longest, label) => {
    return Math.max(longest, label.length);
  }, 0);

  return Math.max(
    MIN_Y_AXIS_WIDTH,
    Math.ceil(
      (longestLabelLength + Y_AXIS_LABEL_EXTRA_CHAR_COUNT) *
        Y_AXIS_LABEL_CHAR_WIDTH,
    ) + Y_AXIS_LABEL_HORIZONTAL_PADDING,
  );
}

function getNiceNumber(value: number, round: boolean): number {
  if (value === 0) {
    return 0;
  }

  const exponent = Math.floor(Math.log10(Math.abs(value)));
  const fraction = Math.abs(value) / 10 ** exponent;

  let niceFraction: number;

  if (round) {
    if (fraction < 1.5) {
      niceFraction = 1;
    } else if (fraction < 3) {
      niceFraction = 2;
    } else if (fraction < 7) {
      niceFraction = 5;
    } else {
      niceFraction = 10;
    }
  } else if (fraction <= 1) {
    niceFraction = 1;
  } else if (fraction <= 2) {
    niceFraction = 2;
  } else if (fraction <= 5) {
    niceFraction = 5;
  } else {
    niceFraction = 10;
  }

  return niceFraction * 10 ** exponent;
}

function getStepPrecision(step: number): number {
  if (!Number.isFinite(step) || step <= 0) {
    return 0;
  }

  return Math.max(0, -Math.floor(Math.log10(step)));
}

function roundToPrecision(value: number, precision: number): number {
  return Number(value.toFixed(precision));
}

function getPopoverTranslateTransform(
  side: string,
  distance: number,
  scale: number,
): string {
  switch (side) {
    case 'top':
      return `translateY(${distance}px) scale(${scale})`;
    case 'bottom':
      return `translateY(${-distance}px) scale(${scale})`;
    case 'left':
      return `translateX(${distance}px) scale(${scale})`;
    case 'right':
      return `translateX(${-distance}px) scale(${scale})`;
    default:
      return `translateY(${-distance}px) scale(${scale})`;
  }
}

function buildYAxisScale(
  values: number[],
  tickCount = Y_AXIS_TICK_COUNT,
): { domain: [number, number]; ticks: number[] } {
  if (!values.length) {
    return {
      domain: [0, 0],
      ticks: [0],
    };
  }

  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);

  if (minValue === maxValue) {
    const paddingBase = Math.abs(minValue) || 1;
    const padding = getNiceNumber(paddingBase * 0.1, true) || 1;
    const lowerBound = roundToPrecision(minValue - padding, 6);
    const upperBound = roundToPrecision(maxValue + padding, 6);

    return {
      domain: [lowerBound, upperBound],
      ticks: [lowerBound, minValue, upperBound],
    };
  }

  const roughStep = (maxValue - minValue) / Math.max(tickCount - 1, 1);
  const step = getNiceNumber(roughStep, true) || 1;
  const precision = getStepPrecision(step);
  const niceMin = roundToPrecision(
    Math.floor(minValue / step) * step,
    precision,
  );
  const niceMax = roundToPrecision(
    Math.ceil(maxValue / step) * step,
    precision,
  );
  const ticks: number[] = [];

  for (
    let tick = niceMin;
    tick <= niceMax + step / 2;
    tick = roundToPrecision(tick + step, precision)
  ) {
    ticks.push(roundToPrecision(tick, precision));
  }

  return {
    domain: [niceMin, niceMax],
    ticks,
  };
}

type BaseChartRow = {
  timestamp: number;
  x: number;
};

type ComparisonChartRow = BaseChartRow & {
  [key: string]: number | undefined;
};

type PrimaryChartRow = TimeseriesPoint & {
  x: number;
};

type ChartRow = PrimaryChartRow | ComparisonChartRow;

type ComparisonTooltipEntry = {
  color?: string;
  dataKey?: string;
  name?: string;
  payload?: ComparisonChartRow;
  value?: number;
};

type ComparisonCard = {
  id: ComparisonIndexId;
  label: string;
  subtitle?: string;
  isError?: boolean;
  isLoading?: boolean;
  latestPercentChange?: number;
  latestRawValue?: number;
};

type ComparisonOption = {
  id: ComparisonIndexId;
  label: string;
  subtitle?: string;
};

type ComparisonControls = {
  cards: ComparisonCard[];
  availableOptions: ComparisonOption[];
  canAddComparison: boolean;
  isAddComparisonDisabled?: boolean;
  onRemoveComparison: (id: ComparisonIndexId) => void;
  onSelectComparison: (id: ComparisonIndexId) => void;
};

const COMPARE_SERIES_COLORS = [
  'var(--color-green-60, #4caf6b)',
  'var(--color-blue-70, #2b6cb0)',
  'var(--color-slate-70, #3f5873)',
];

const COMPARISON_CONTEXT_DATA_KEY_SUFFIX = '__context';

function getComparisonContextDataKey(seriesId: string): string {
  return `${seriesId}${COMPARISON_CONTEXT_DATA_KEY_SUFFIX}`;
}

function isComparisonContextDataKey(dataKey?: string): boolean {
  return dataKey?.endsWith(COMPARISON_CONTEXT_DATA_KEY_SUFFIX) ?? false;
}

function getComparisonSeriesId(dataKey?: string): string {
  return dataKey && isComparisonContextDataKey(dataKey)
    ? dataKey.slice(0, -COMPARISON_CONTEXT_DATA_KEY_SUFFIX.length)
    : (dataKey ?? '');
}

function buildComparisonChartRows(
  series: NormalizedTimeseriesSeries[],
  compressXAxis: boolean,
): ComparisonChartRow[] {
  const timestamps = Array.from(
    new Set(
      series.flatMap((entry) => entry.points.map((point) => point.timestamp)),
    ),
  ).sort((left, right) => left - right);

  return timestamps.map((timestamp, index) => {
    const row: ComparisonChartRow = {
      timestamp,
      x: compressXAxis ? index : timestamp,
    };

    for (const entry of series) {
      const points = entry.points.filter(
        (seriesPoint) => seriesPoint.timestamp === timestamp,
      );

      if (!points.length) {
        continue;
      }

      for (const point of points) {
        const dataKey = point.isContextOnly
          ? getComparisonContextDataKey(entry.id)
          : entry.id;

        row[dataKey] = point.percentChange;
        row[`${dataKey}__raw`] = point.sourcePoint.closePrice;
        row[`${entry.id}__raw`] = point.sourcePoint.closePrice;
      }
    }

    return row;
  });
}

function buildPrimaryChartRows(
  points: TimeseriesPoint[],
  compressXAxis: boolean,
): PrimaryChartRow[] {
  return points.map((point, index) => ({
    ...point,
    x: compressXAxis ? index : point.timestamp,
  }));
}

function shouldCompressTimeAxis(timeframe: ChartTimeframeId): boolean {
  return timeframe === 'INTRADAY' || timeframe === '1W';
}

function resolveXAxisTimestamp(value: number, rows: ChartRow[]): number {
  if (!rows.length) {
    return value;
  }

  const roundedIndex = Math.round(value);
  const clampedIndex = Math.min(Math.max(roundedIndex, 0), rows.length - 1);
  return rows[clampedIndex]?.timestamp ?? value;
}

function getComparisonValues(series: NormalizedTimeseriesSeries[]): number[] {
  return series.flatMap((entry) =>
    entry.points.flatMap((point) =>
      point.isContextOnly ? [] : [point.percentChange],
    ),
  );
}

function getNormalizedTooltipEntries(
  payload: ComparisonTooltipEntry[],
  row: ComparisonChartRow,
): ComparisonTooltipEntry[] {
  const entriesBySeries = new Map<string, ComparisonTooltipEntry>();

  for (const entry of payload) {
    const dataKey = entry.dataKey;
    const seriesId = getComparisonSeriesId(dataKey);

    if (!dataKey || !seriesId || row[dataKey] == null) {
      continue;
    }

    const existingEntry = entriesBySeries.get(seriesId);

    if (!existingEntry) {
      entriesBySeries.set(seriesId, entry);
      continue;
    }

    if (
      isComparisonContextDataKey(existingEntry.dataKey) &&
      !isComparisonContextDataKey(dataKey)
    ) {
      entriesBySeries.set(seriesId, entry);
    }
  }

  return [...entriesBySeries.values()];
}

function getRawValues(points: TimeseriesPoint[]): number[] {
  return points.map((point) => point.closePrice);
}

function formatTooltipTimestamp(point: TimeseriesPoint): string {
  const date = new Date(point.timestamp);

  if (point.rawTime) {
    return TOOLTIP_DAY_TIME_FORMATTER.format(date);
  }

  return TOOLTIP_DAY_FORMATTER.format(date);
}

function formatTooltipValue(
  label: string,
  value: number | undefined,
  formatter: (value: number | null | undefined) => string | undefined,
) {
  return (
    <div className="flex items-center justify-between gap-12">
      <span className="text-fg-muted">{label}</span>
      <span className="text-fg font-medium">{formatter(value) ?? DASH}</span>
    </div>
  );
}

function formatTooltipCloseValue(value: number | undefined): string {
  return formatCurrency(value) ?? DASH;
}

function ChartTooltip({
  active,
  payload,
  fields = DEFAULT_TOOLTIP_FIELDS,
}: {
  active?: boolean;
  payload?: Array<{ payload: TimeseriesPoint }>;
  fields?: TooltipField[];
}) {
  if (!active || !payload?.length) {
    return null;
  }

  const point = payload[0]?.payload;

  if (!point) {
    return null;
  }

  const isCloseOnly = fields.length === 1 && fields[0] === 'close';
  const closeLabel = isCloseOnly ? 'Price' : 'Close';

  return (
    <div
      data-testid="chart-tooltip"
      className="border-divider-subtle bg-gray-10 min-w-180 rounded border p-12 text-sm shadow-(--shadow-card)"
    >
      <p className="text-fg-header mb-8 font-semibold">
        {formatTooltipTimestamp(point)}
      </p>
      <div className="flow flow-inner-4">
        {fields.includes('close') &&
          formatTooltipValue(closeLabel, point.closePrice, formatCurrency)}
        {fields.includes('open') &&
          formatTooltipValue('Open', point.openPrice, formatCurrency)}
        {fields.includes('high') &&
          formatTooltipValue('High', point.highPrice, formatCurrency)}
        {fields.includes('low') &&
          formatTooltipValue('Low', point.lowPrice, formatCurrency)}
        {fields.includes('volume') &&
          formatTooltipValue('Volume', point.volume, formatCompact)}
      </div>
    </div>
  );
}

function ComparisonChartTooltip({
  active,
  payload,
  baselineTimestamp,
}: {
  active?: boolean;
  payload?: ComparisonTooltipEntry[];
  baselineTimestamp?: number;
}) {
  if (!active || !payload?.length) {
    return null;
  }

  const row = payload[0]?.payload;

  if (!row) {
    return null;
  }

  const tooltipEntries = getNormalizedTooltipEntries(payload, row);

  if (!tooltipEntries.length) {
    return null;
  }

  const shouldShowBaselineTimestamp =
    baselineTimestamp !== undefined && row.timestamp >= baselineTimestamp;

  return (
    <div className="border-divider-subtle bg-gray-10 min-w-[220px] rounded border p-12 text-sm shadow-(--shadow-card)">
      <p className="text-fg-header mb-8 font-semibold">
        {TOOLTIP_DAY_FORMATTER.format(new Date(row.timestamp))}
      </p>

      <div className="flow flow-inner-8">
        {tooltipEntries.map((entry) => {
          const seriesId = getComparisonSeriesId(entry.dataKey);
          const rawValue =
            (entry.dataKey ? row[`${entry.dataKey}__raw`] : undefined) ??
            row[`${seriesId}__raw`];
          const isContextPoint = isComparisonContextDataKey(entry.dataKey);

          return (
            <div
              key={entry.dataKey}
              className="flex items-start justify-between gap-16"
            >
              <div className="flex items-center gap-8">
                <span
                  className="mt-3 inline-block size-10 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
                <div className="flex flex-col">
                  <span className="text-fg-muted">{entry.name}</span>
                  <span className="text-fg-muted text-xs">
                    {formatCurrency(rawValue) ?? DASH}
                  </span>
                </div>
              </div>
              <span className="text-fg font-medium">
                {isContextPoint
                  ? formatTooltipCloseValue(rawValue)
                  : formatSignedPercentChange(entry.value)}
              </span>
            </div>
          );
        })}
      </div>
      {shouldShowBaselineTimestamp && baselineTimestamp ? (
        <p className="text-fg-muted mt-8 text-xs">
          Since {TOOLTIP_DAY_FORMATTER.format(new Date(baselineTimestamp))}
        </p>
      ) : null}
    </div>
  );
}

function EmptyState({ children }: { children: ReactNode }) {
  return (
    <div className="border-divider-subtle bg-slate-10 text-fg-muted h-chart flex items-center justify-center rounded border border-dashed px-16 text-center text-sm">
      {children}
    </div>
  );
}

function CompactEmptyState({ children }: { children: ReactNode }) {
  return (
    <div className="border-divider-subtle bg-slate-10 text-fg-muted h-chart-compact flex items-center justify-center rounded border border-dashed px-16 text-center text-sm">
      {children}
    </div>
  );
}

function LastCloseLabel({
  viewBox,
  value,
}: SVGProps<SVGTextElement> & {
  viewBox?: { x?: number; y?: number; width?: number };
  value: number;
}) {
  const x = (viewBox?.x ?? 0) + (viewBox?.width ?? 0) - 8;
  const lineY = viewBox?.y ?? 0;
  const labelHeight = 34;
  const hasRoomAbove = lineY >= labelHeight + 12;
  const titleY = hasRoomAbove ? lineY - 22 : lineY + 18;
  const valueY = hasRoomAbove ? lineY - 6 : lineY + 34;

  return (
    <g>
      <text
        x={x}
        y={titleY}
        textAnchor="end"
        fill="var(--color-slate-90, #11233e)"
        fontSize="12"
      >
        Last Close Price
      </text>
      <text
        x={x}
        y={valueY}
        textAnchor="end"
        fill="var(--color-slate-90, #11233e)"
        fontSize="12"
        fontWeight="700"
      >
        {formatCurrency(value)}
      </text>
    </g>
  );
}

const CompareToSection = memo(function CompareToSectionView({
  comparisonControls,
}: {
  comparisonControls: ComparisonControls;
}) {
  const comparePickerId = useId();
  const [isComparePickerOpen, setIsComparePickerOpen] = useState(false);
  const compareTriggerRef = useRef<HTMLButtonElement | null>(null);
  const comparePickerRef = useRef<HTMLDivElement | null>(null);
  const { refs, floatingStyles, context } = useFloating({
    open: isComparePickerOpen,
    onOpenChange: (open) => {
      if (!open) {
        setIsComparePickerOpen(false);
      }
    },
    placement: 'bottom-start',
    strategy: 'fixed',
    middleware: [offset(8), flip(), shift({ padding: 16 })],
    whileElementsMounted: autoUpdate,
  });
  const { isMounted: isComparePickerMounted, styles: comparePickerStyles } =
    useTransitionStyles(context, {
      duration: {
        open: 160,
        close: 120,
      },
      initial: ({ side }) => ({
        opacity: 0,
        transform: getPopoverTranslateTransform(side, 6, 0.98),
      }),
      open: {
        opacity: 1,
        transform: 'translateY(0) scale(1)',
      },
      close: ({ side }) => ({
        opacity: 0,
        transform: getPopoverTranslateTransform(side, 4, 0.99),
      }),
    });
  const shouldRenderComparePicker =
    isComparePickerOpen || isComparePickerMounted;

  useEffect(() => {
    if (comparisonControls.isAddComparisonDisabled && isComparePickerOpen) {
      setIsComparePickerOpen(false);
    }
  }, [comparisonControls.isAddComparisonDisabled, isComparePickerOpen]);

  useDismissibleOverlay({
    active: isComparePickerOpen,
    insideRefs: [compareTriggerRef, comparePickerRef],
    onDismiss: () => setIsComparePickerOpen(false),
  });

  return (
    <section className="flow flow-inner-8" aria-label="Compare to">
      <h3 className="text-fg-header text-sm font-bold">Compare to</h3>

      <div className="grid gap-8 md:grid-cols-2">
        {comparisonControls.cards.map((card) => (
          <div
            key={card.id}
            className="group/card border-slate-20 bg-gray-10 relative flex min-h-112 flex-col gap-4 rounded border p-16"
          >
            <button
              type="button"
              className="text-fg-muted hover:text-fg absolute top-8 right-8 cursor-pointer p-4 opacity-0 transition-opacity group-hover/card:opacity-100"
              onClick={() => comparisonControls.onRemoveComparison(card.id)}
              aria-label={`Remove ${card.label} comparison`}
            >
              <Icon id="close" size={16} />
            </button>

            <div className="pr-24">
              <p className="text-fg-header text-sm font-semibold">
                {card.label}
              </p>
            </div>

            <div className="mt-auto flex flex-col gap-2">
              {card.isLoading ? (
                <span className="text-fg-muted text-sm">Loading…</span>
              ) : card.isError ? (
                <span className="text-fg-muted text-sm">
                  Could not load comparison data.
                </span>
              ) : (
                <>
                  <p className="text-fg-header text-base font-semibold">
                    {formatSignedPercentChange(card.latestPercentChange)}
                  </p>
                  <p className="text-fg-muted text-sm">
                    Latest {formatDecimal(card.latestRawValue) ?? DASH}
                  </p>
                </>
              )}
            </div>
          </div>
        ))}

        {comparisonControls.canAddComparison ? (
          <div className="min-h-112">
            <button
              ref={(node) => {
                compareTriggerRef.current = node;
                refs.setReference(node);
              }}
              type="button"
              disabled={comparisonControls.isAddComparisonDisabled}
              aria-haspopup="dialog"
              aria-expanded={isComparePickerOpen}
              aria-controls={isComparePickerOpen ? comparePickerId : undefined}
              className="bg-gray-10 border-slate-20 hover:bg-slate-10 disabled:text-fg-muted disabled:hover:bg-gray-10 flex min-h-full w-full cursor-pointer items-center gap-12 rounded border p-16 text-left transition-colors disabled:cursor-not-allowed"
              onClick={() => setIsComparePickerOpen((open) => !open)}
            >
              <span className="border-slate-10 bg-gray-10 disabled:bg-slate-20 rounded-full border p-10 shadow-sm">
                <Icon id="plus" size={20} className="text-link-fg-hover" />
              </span>
              <span className="text-fg-header text-base font-semibold">
                Add to Compare
              </span>
            </button>

            {shouldRenderComparePicker ? (
              <FloatingPortal>
                <FloatingFocusManager
                  context={context}
                  modal
                  returnFocus
                  initialFocus={refs.floating}
                >
                  <div
                    ref={(node) => {
                      comparePickerRef.current = node;
                      refs.setFloating(node);
                    }}
                    id={comparePickerId}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Compare indexes"
                    className="z-20"
                    tabIndex={-1}
                    style={{
                      ...floatingStyles,
                      width: 'min(280px, calc(100vw - 32px))',
                    }}
                  >
                    <div
                      style={comparePickerStyles}
                      className="border-slate-20 bg-gray-10 relative rounded border p-8 drop-shadow-[0_8px_16px_rgba(0,52,83,0.06)] will-change-transform motion-reduce:transition-none"
                    >
                      <button
                        type="button"
                        className="text-fg-muted hover:text-fg sr-only absolute top-4 right-4 rounded p-4 focus:not-sr-only"
                        aria-label="Close compare menu"
                        onClick={() => setIsComparePickerOpen(false)}
                      >
                        <Icon id="close" size={14} />
                      </button>

                      <div className="flow flow-inner-8">
                        {comparisonControls.availableOptions.map((option) => (
                          <button
                            key={option.id}
                            type="button"
                            className="border-slate-20 hover:bg-unchanged-bg flex w-full cursor-pointer items-center justify-between rounded border px-12 py-10 text-left transition-colors"
                            onClick={() => {
                              comparisonControls.onSelectComparison(option.id);
                            }}
                          >
                            <span className="text-fg-header text-sm font-semibold">
                              {option.label}
                            </span>
                            <Icon
                              id="plus"
                              size={16}
                              className="text-fg-muted"
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </FloatingFocusManager>
              </FloatingPortal>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
});

interface TimeseriesChartProps {
  data: TimeseriesPoint[];
  normalizedSeries?: NormalizedTimeseriesSeries[];
  comparisonControls?: ComparisonControls;
  activeTimeframe?: ChartTimeframeId;
  displayedTimeframe?: ChartTimeframeId;
  xAxisDomain?: [number, number];
  onTimeframeChange?: (nextTimeframe: ChartTimeframeId) => void;
  tabs?: Array<{ id: ChartTimeframeId; label: string; group?: string }>;
  maxPoints?: number;
  isLoading?: boolean;
  showTabs?: boolean;
  showAxes?: boolean;
  variant?: 'default' | 'compact';
  tooltipFields?: TooltipField[];
  /** From the detail endpoint. Used for the last-close reference line. */
  previousClose?: number | null;
}

export function TimeseriesChart({
  data,
  normalizedSeries,
  comparisonControls,
  activeTimeframe = '5Y',
  displayedTimeframe,
  xAxisDomain,
  onTimeframeChange,
  tabs,
  maxPoints,
  isLoading = false,
  showTabs = true,
  showAxes = true,
  variant = 'default',
  tooltipFields = DEFAULT_TOOLTIP_FIELDS,
  previousClose,
}: TimeseriesChartProps) {
  const isCompact = variant === 'compact';
  const xAxisTimeframe = displayedTimeframe ?? activeTimeframe;
  const compressXAxis = shouldCompressTimeAxis(xAxisTimeframe);
  const hasComparisonSeries = Boolean(
    normalizedSeries && normalizedSeries.length > 1,
  );
  const primaryChartPoints = useMemo(() => {
    if (data.length === 0) {
      return [];
    }

    return typeof maxPoints === 'number' && Number.isFinite(maxPoints)
      ? downsampleTimeseries(data, maxPoints)
      : data;
  }, [data, maxPoints]);
  const chartData = useMemo<ChartRow[]>(() => {
    return hasComparisonSeries
      ? buildComparisonChartRows(normalizedSeries ?? [], compressXAxis)
      : buildPrimaryChartRows(primaryChartPoints, compressXAxis);
  }, [
    compressXAxis,
    hasComparisonSeries,
    normalizedSeries,
    primaryChartPoints,
  ]);
  const hasData = chartData.length > 0;

  // Closes `mdc:chart-render` on the commit that puts the chart SVG in the DOM.
  // No-ops unless a chart section opened the measure.
  useMeasureOnCommit(MDC_MEASURE.chartRender, hasData);

  const comparisonBaselineTimestamp = useMemo(() => {
    if (!hasComparisonSeries) {
      return undefined;
    }

    return normalizedSeries?.[0]?.points.find((point) => !point.isContextOnly)
      ?.timestamp;
  }, [hasComparisonSeries, normalizedSeries]);

  const yAxisValues = useMemo(() => {
    return hasComparisonSeries
      ? getComparisonValues(normalizedSeries ?? [])
      : getRawValues(primaryChartPoints);
  }, [hasComparisonSeries, normalizedSeries, primaryChartPoints]);

  const yAxisScale = useMemo(() => {
    return buildYAxisScale(yAxisValues);
  }, [yAxisValues]);

  const yAxisWidth = useMemo(() => {
    const yAxisLabels = hasComparisonSeries
      ? yAxisScale.ticks.map((value) => formatCompareYAxisTick(value))
      : yAxisScale.ticks.map((value) => formatYAxisTick(value));

    return getYAxisWidth(yAxisLabels);
  }, [hasComparisonSeries, yAxisScale.ticks]);
  const lastCloseValue = hasComparisonSeries ? undefined : previousClose;
  const effectiveTooltipFields: TooltipField[] = compressXAxis
    ? ['close']
    : tooltipFields;
  const chartHeightClass = isCompact ? 'h-chart-compact' : 'h-chart';
  const chartContainerClasses = cn(
    'flow min-w-0',
    isCompact ? 'flow-inner-12' : 'flow-inner-16 p-gap-inset px-16',
  );
  const chartMargins = isCompact
    ? { top: 12, right: 0, bottom: 0, left: 0 }
    : { top: 40, right: 8, bottom: 8, left: CHART_LEFT_MARGIN };
  const EmptyStateComponent = isCompact ? CompactEmptyState : EmptyState;

  return (
    <div className={chartContainerClasses} aria-busy={isLoading}>
      {showTabs && tabs && onTimeframeChange ? (
        <div className={isLoading ? 'pointer-events-none' : undefined}>
          <Tabs
            tabs={tabs}
            activeTab={activeTimeframe}
            onChange={onTimeframeChange}
            aria-label="Chart timeframe"
          />
        </div>
      ) : null}

      <div
        className={cn(
          'relative min-w-0',
          chartHeightClass,
          // Nudge the rightmost X-axis tick label left slightly so it doesn't
          // render past the chart's edge.
          '[&_.recharts-xAxis-tick-labels_.recharts-cartesian-axis-tick-label:last-child]:-translate-x-[4px]',
        )}
      >
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={chartMargins}
              className={
                isLoading
                  ? 'opacity-60 transition-opacity'
                  : 'transition-opacity'
              }
            >
              <defs>
                <linearGradient
                  id="timeseries-fill"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor="#71d85f" stopOpacity={0.75} />
                  <stop offset="100%" stopColor="#71d85f" stopOpacity={0.08} />
                </linearGradient>
              </defs>

              <CartesianGrid
                stroke="var(--color-divider-subtle, #d6dde8)"
                strokeDasharray="2 4"
                vertical={false}
              />

              <XAxis
                hide={!showAxes}
                dataKey="x"
                domain={
                  !compressXAxis && xAxisDomain
                    ? xAxisDomain
                    : ['dataMin', 'dataMax']
                }
                minTickGap={32}
                tick={{
                  fontSize: AXIS_FONT_SIZE,
                  fill: 'var(--color-slate-80, #1c355a)',
                }}
                tickFormatter={(value: number) =>
                  formatXAxisTick(
                    xAxisTimeframe,
                    compressXAxis
                      ? resolveXAxisTimestamp(value, chartData)
                      : value,
                  )
                }
                tickMargin={8}
                tickLine={false}
                axisLine={false}
                type="number"
              />

              <YAxis
                hide={!showAxes}
                axisLine={false}
                tickLine={false}
                tick={{
                  fontSize: AXIS_FONT_SIZE,
                  fill: 'var(--color-slate-80, #1c355a)',
                }}
                tickFormatter={
                  hasComparisonSeries ? formatCompareYAxisTick : formatYAxisTick
                }
                tickMargin={10}
                width={yAxisWidth}
                domain={yAxisScale.domain}
                ticks={yAxisScale.ticks}
              />

              <Tooltip
                content={
                  hasComparisonSeries ? (
                    <ComparisonChartTooltip
                      baselineTimestamp={comparisonBaselineTimestamp}
                    />
                  ) : (
                    <ChartTooltip fields={effectiveTooltipFields} />
                  )
                }
                cursor={{ stroke: '#aac6db', strokeDasharray: '4 4' }}
                isAnimationActive={false}
              />

              {!isCompact && lastCloseValue != null ? (
                <ReferenceLine
                  ifOverflow="extendDomain"
                  stroke="#2b4f72"
                  strokeDasharray="6 4"
                  y={lastCloseValue}
                  label={(props) => (
                    <LastCloseLabel {...props} value={lastCloseValue} />
                  )}
                />
              ) : null}

              {hasComparisonSeries ? (
                (normalizedSeries ?? []).map((series, index) => (
                  <Fragment key={series.id}>
                    {series.kind === 'comparison' ? (
                      <Area
                        key={`${series.id}-context`}
                        dataKey={getComparisonContextDataKey(series.id)}
                        name={series.label}
                        dot={false}
                        fill={COMPARE_SERIES_COLORS[index]}
                        fillOpacity={0}
                        isAnimationActive={false}
                        stroke={COMPARE_SERIES_COLORS[index]}
                        strokeDasharray="6 4"
                        strokeOpacity={0.45}
                        strokeWidth={2}
                        type="linear"
                      />
                    ) : null}

                    <Area
                      key={series.id}
                      dataKey={series.id}
                      name={series.label}
                      dot={false}
                      fill={COMPARE_SERIES_COLORS[index]}
                      fillOpacity={0}
                      isAnimationActive={false}
                      stroke={COMPARE_SERIES_COLORS[index]}
                      strokeWidth={2}
                      type="linear"
                    />
                  </Fragment>
                ))
              ) : (
                <Area
                  dataKey="closePrice"
                  dot={false}
                  fill="url(#timeseries-fill)"
                  fillOpacity={1}
                  isAnimationActive={false}
                  stroke="var(--color-green-60, #4caf6b)"
                  strokeWidth={2}
                  type="linear"
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <EmptyStateComponent>
            <div className="flex flex-col items-center justify-center gap-8">
              <Icon id="exclamation" className="fill-slate-60" />
              <span className="text-slate-60">
                No chart data is available for this range.
              </span>
            </div>
          </EmptyStateComponent>
        )}

        {isLoading ? (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded">
            <Spinner label="Loading chart data" />
          </div>
        ) : null}
      </div>

      {comparisonControls ? (
        <CompareToSection comparisonControls={comparisonControls} />
      ) : null}
    </div>
  );
}

export { CHART_HEIGHT };
